import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import { api } from "@/lib/apiClient";
import type { Account } from "@/types/camping";

export interface EditAccountModalProps {
  open: boolean;
  account: Account | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
}

export function EditAccountModal({
  open,
  account,
  onClose,
  onSaved,
  canEdit,
}: EditAccountModalProps) {
  const [form, setForm] = useState({ account_number: "", name: "", is_active: true });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !account) return;
    setForm({
      account_number: account.account_number,
      name: account.name,
      is_active: account.is_active,
    });
    setError(null);
    setSubmitting(false);
  }, [open, account]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!account || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/api/v1/accounts/${account.id}`, {
        account_number: form.account_number.trim(),
        name: form.name.trim(),
        is_active: form.is_active,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={account ? `${account.account_number} – ${account.name}` : "Konto"}
      description="Erlöskonto bearbeiten"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Kontonummer
          <input
            required
            disabled={!canEdit}
            inputMode="numeric"
            pattern="[0-9]+"
            title="Nur Ziffern"
            className="eh-input w-40"
            value={form.account_number}
            onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Bezeichnung
          <input
            required
            disabled={!canEdit}
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
          Aktiv
        </label>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        {account ? <AuditHistory entityType="account" entityId={account.id} /> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Schließen
          </Button>
          {canEdit ? (
            <Button type="submit" loading={submitting}>
              Speichern
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
